/**
 * WebSocketAuthBridge - Authentication bridge for WebSocket connections
 * date_of_change: 2025-09-24
 *
 * Integrates Clerk authentication with WebSocket service for secure multiplayer connections.
 * Handles token management, user identification, and authentication state changes.
 */

import { useAuth, useUser } from '@clerk/clerk-react';
import { useEffect, useCallback, useState } from 'react';
import webSocketService from './WebSocketService';

/**
 * Hook to manage WebSocket authentication with Clerk
 *
 * This hook:
 * 1. Monitors Clerk authentication state changes
 * 2. Automatically connects/disconnects WebSocket based on auth state
 * 3. Passes user tokens and information to WebSocket service
 * 4. Provides authentication status for components
 */
export const useWebSocketAuth = () => {
  const { isSignedIn, isLoaded, userId, getToken } = useAuth();
  const { user } = useUser();
  const [webSocketStatus, setWebSocketStatus] = useState('disconnected');
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    // Replacement sockets need a current Clerk credential. Keeping the token
    // provider in the auth bridge avoids coupling the transport service to
    // React or Clerk while preserving automatic reconnect. date_of_change: 2026-08-25
    webSocketService.setAuthTokenProvider(() => getToken());
    return () => webSocketService.setAuthTokenProvider(null);
  }, [getToken]);

  /**
   * Connect to WebSocket with authentication
   * @date_of_change: 2025-10-01 - Optimized to avoid unnecessary Clerk API calls on reconnections
   */
  const connectWithAuth = useCallback(async (useStoredToken = false) => {
    if (!isSignedIn || !userId || !user) {
      console.log('[WebSocketAuth] Cannot connect - user not authenticated');
      return false;
    }

    try {
      setWebSocketStatus('connecting');
      setAuthError(null);

      // @date_of_change: 2025-10-01 - Avoid unnecessary Clerk API calls during reconnections
      // Only get a fresh token if we don't have stored auth data or if explicitly requested
      let token;
      const currentStoredAuth = webSocketService.getStoredAuthData();

      if (useStoredToken && currentStoredAuth?.token) {
        token = currentStoredAuth.token;
        console.log('[WebSocketAuth] Using stored token for reconnection (no Clerk API call)');
      } else {
        token = await getToken();
        console.log('[WebSocketAuth] Got fresh token from Clerk API');
      }

      // Prepare authentication data for WebSocket
      const authData = {
        // Clerk token for backend verification
        token: token,

        // User identification
        userId: userId,
        clerkUserId: userId,

        // User profile data
        userData: {
          id: userId,
          firstName: user.firstName,
          lastName: user.lastName,
          emailAddress: user.primaryEmailAddress?.emailAddress,
          username: user.username,
          imageUrl: user.imageUrl,
          fullName: user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        },

        // Additional metadata
        connectedAt: new Date().toISOString(),
        userAgent: navigator.userAgent
      };

      // Connect WebSocket with authentication using backend consistency
      // @date_of_change: 2025-09-25 - Use consistent backend connection
      await webSocketService.connectWithResolvedBackend(authData);

      setWebSocketStatus('connected');
      console.log('[WebSocketAuth] WebSocket connected with authentication');
      return true;

    } catch (error) {
      console.error('[WebSocketAuth] Connection failed:', error);
      setAuthError(error.message);
      setWebSocketStatus('error');
      return false;
    }
  }, [isSignedIn, userId, user, getToken]);

  /**
   * Disconnect WebSocket
   */
  const disconnect = useCallback(() => {
    console.log('[WebSocketAuth] Disconnecting WebSocket...');
    webSocketService.disconnect();
    setWebSocketStatus('disconnected');
    setAuthError(null);
  }, []);

  /**
   * Handle authentication state changes
   */
  useEffect(() => {
    if (!isLoaded) {
      // Clerk is still loading authentication state
      console.log('[WebSocketAuth] Clerk auth loading...');
      return;
    }

    if (isSignedIn && userId && user) {
      // User is authenticated - connect WebSocket
      console.log('[WebSocketAuth] User authenticated, connecting WebSocket...');
      connectWithAuth();
    } else {
      // User is not authenticated - disconnect WebSocket
      console.log('[WebSocketAuth] User not authenticated, disconnecting WebSocket...');
      disconnect();
    }

    // Cleanup on unmount or auth change
    return () => {
      if (webSocketService.isConnected) {
        console.log('[WebSocketAuth] Cleaning up WebSocket connection...');
        disconnect();
      }
    };
  }, [isLoaded, isSignedIn, userId, user, connectWithAuth, disconnect]);

  /**
   * Set up WebSocket connection event handlers
   */
  useEffect(() => {
    const onConnect = () => {
      console.log('[WebSocketAuth] WebSocket connected');
      setWebSocketStatus('connected');
      setAuthError(null);
    };

    const onDisconnect = (reason) => {
      console.log('[WebSocketAuth] WebSocket disconnected:', reason);
      setWebSocketStatus('disconnected');

      // @date_of_change: 2025-10-01 - WebSocketService now handles authenticated reconnections automatically
      // No need to handle reconnection here - WebSocketService preserves auth during reconnects
    };

    const onError = (error) => {
      console.error('[WebSocketAuth] WebSocket error:', error);
      setAuthError(error.message);
      setWebSocketStatus('error');
    };

    const onReconnect = () => {
      console.log('[WebSocketAuth] WebSocket reconnected');
      setWebSocketStatus('connected');
      setAuthError(null);
    };

    // Add event listeners
    webSocketService.addConnectionCallback('onConnect', onConnect);
    webSocketService.addConnectionCallback('onDisconnect', onDisconnect);
    webSocketService.addConnectionCallback('onError', onError);
    webSocketService.addConnectionCallback('onReconnect', onReconnect);

    // Cleanup event listeners
    return () => {
      webSocketService.removeConnectionCallback('onConnect', onConnect);
      webSocketService.removeConnectionCallback('onDisconnect', onDisconnect);
      webSocketService.removeConnectionCallback('onError', onError);
      webSocketService.removeConnectionCallback('onReconnect', onReconnect);
    };
  }, [isSignedIn, connectWithAuth]);

  /**
   * Manual reconnect function
   */
  const reconnect = useCallback(async () => {
    if (!isSignedIn) {
      console.log('[WebSocketAuth] Cannot reconnect - user not authenticated');
      return false;
    }

    try {
      setWebSocketStatus('connecting');
      setAuthError(null);

      // An explicit reconnect has the same authority requirement as automatic
      // recovery: obtain a current Clerk token before replacing the socket.
      // date_of_change: 2026-08-25
      const success = await connectWithAuth(false);

      if (success) {
        setWebSocketStatus('connected');
      } else {
        setWebSocketStatus('error');
        setAuthError('Reconnection failed');
      }

      return success;
    } catch (error) {
      console.error('[WebSocketAuth] Reconnect failed:', error);
      setAuthError(error.message);
      setWebSocketStatus('error');
      return false;
    }
  }, [isSignedIn, connectWithAuth]);

  /**
   * Get current user info for WebSocket operations
   */
  const getCurrentUserInfo = useCallback(() => {
    if (!isSignedIn || !user) {
      return null;
    }

    return {
      userId: userId,
      clerkUserId: userId,
      firstName: user.firstName,
      lastName: user.lastName,
      emailAddress: user.primaryEmailAddress?.emailAddress,
      username: user.username,
      imageUrl: user.imageUrl,
      fullName: user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
    };
  }, [isSignedIn, userId, user]);

  return {
    // Connection state
    webSocketStatus, // 'disconnected', 'connecting', 'connected', 'error'
    isConnected: webSocketStatus === 'connected',
    isConnecting: webSocketStatus === 'connecting',
    authError,

    // Connection methods
    connect: connectWithAuth,
    disconnect,
    reconnect,

    // User information
    currentUser: getCurrentUserInfo(),

    // WebSocket service reference
    webSocketService,

    // Clerk auth state
    isSignedIn,
    isAuthLoaded: isLoaded
  };
};

/**
 * Utility function to get auth headers for API requests
 * Can be used with regular HTTP requests that need the same auth as WebSocket
 */
export const useAuthHeaders = () => {
  const { getToken } = useAuth();

  const getHeaders = useCallback(async () => {
    try {
      const token = await getToken();
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
    } catch (error) {
      console.error('[WebSocketAuth] Failed to get auth headers:', error);
      return {
        'Content-Type': 'application/json'
      };
    }
  }, [getToken]);

  return { getAuthHeaders: getHeaders };
};

export default useWebSocketAuth;
